// Client for the public storefront API (sellers-worker /api/store/*).
// The shop's database is the source of truth; this page only ever reads the
// catalog and submits an order — prices and stock are decided server-side.

const API = process.env.REACT_APP_STORE_API || "https://api.sellers.thecreaksofficial.com";
const PREVIEW_KEY = "creaks-store-preview";

// ?preview=<key> (shared from the admin while the store is offline) sticks for
// the browser session, so it survives navigating to the order page.
export function previewKey() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("preview");
    if (fromUrl) sessionStorage.setItem(PREVIEW_KEY, fromUrl);
    return fromUrl || sessionStorage.getItem(PREVIEW_KEY) || "";
  } catch {
    return "";
  }
}

async function call(path, { method = "GET", body } = {}) {
  const preview = previewKey();
  const sep = path.includes("?") ? "&" : "?";
  const res = await fetch(`${API}${path}${preview ? `${sep}preview=${encodeURIComponent(preview)}` : ""}`, {
    method,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Something went wrong (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const storeApi = {
  config: () => call("/api/store/config"),
  // { products, collections, sales }
  catalog: () => call("/api/store/products"),
  quote: (body) => call("/api/store/quote", { method: "POST", body }),
  checkout: (body) => call("/api/store/checkout", { method: "POST", body }),
  notify: (email, topic) => call("/api/store/notify", { method: "POST", body: { email, topic } }),
  inquiry: (body) => call("/api/store/inquiry", { method: "POST", body }),
  order: (token) => call(`/api/store/orders/${encodeURIComponent(token)}`),
  cancelOrder: (token) => call(`/api/store/orders/${encodeURIComponent(token)}/cancel`, { method: "POST", body: {} }),
  payOrder: (token, method) => call(`/api/store/orders/${encodeURIComponent(token)}/pay`, { method: "POST", body: { method } }),
};

// ---- browser storage (never required: everything works without it) --------

function read(storage, key, fallback) {
  try {
    const v = JSON.parse(window[storage].getItem(key));
    return v ?? fallback;
  } catch {
    return fallback;
  }
}
function write(storage, key, value) {
  try {
    if (value == null) window[storage].removeItem(key);
    else window[storage].setItem(key, JSON.stringify(value));
  } catch {
    // storage blocked — it just won't survive a reload
  }
}

// A cart line remembers what it looked like when added ({ id, qty, snap }),
// so an item that later sells out or disappears can still be shown by name.
const CART_KEY = "creaks-cart";
export const loadCart = () => {
  const v = read("localStorage", CART_KEY, []);
  return Array.isArray(v) ? v.filter((l) => l && l.id && l.qty > 0) : [];
};
export const saveCart = (lines) => write("localStorage", CART_KEY, lines);

const CODE_KEY = "creaks-discount";
export const loadCode = () => read("sessionStorage", CODE_KEY, "") || "";
export const saveCode = (code) => write("sessionStorage", CODE_KEY, code || null);

const FORM_KEY = "creaks-checkout";
export const loadCheckoutForm = () => read("sessionStorage", FORM_KEY, null);
export const saveCheckoutForm = (form) => write("sessionStorage", FORM_KEY, form);
